import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Icon, EmptyState, Skeleton, Text } from '../../../../components';
import type { Player } from '../../../../mock-data/types';
import { openInApp } from '../../../creatives/components/LandingPageComposer/link-for';
import type { CameFrom } from '../../attribution';
import { money, playerLabel, prettyDate } from '../../format';
import { channelOf, sourceOf } from '../../roster-view';
import { useSourceLabel, type SourceLabeller } from '../../useSourceLabel';
import { PlayerAvatar } from '../PlayerAvatar';
import {
  List,
  Card,
  CameCell,
  FromChip,
  FromKind,
  FromName,
  FromVia,
  FromViaWord,
  FromViaLink,
  FromMuted,
  SkCame,
  AvatarCell,
  EmailCell,
  EmailRow,
  EmailText,
  CardMeta,
  MetaText,
  CopyBtn,
  Stats,
  MetaCell,
  CellLabel,
  MoneyValue,
  NoValue,
  STAT_COLUMNS,
  SkCard,
  SkIdentity,
  SkStats,
} from './PlayersTable.styles';

export type PlayersTableProps = {
  players: Player[];
  /** Roster still loading — show placeholder cards instead of data. */
  loading?: boolean;
  /** Copy a player's email to the clipboard (drives the page toast). */
  onCopyEmail: (email: string) => void;
  /** Open a player's detail sheet (card tap / Enter / Space). */
  onSelect: (player: Player) => void;
  /** Message for the empty list (differs for "no players yet" vs "no search match"). */
  emptyText?: string;
  /** Where a player came from (see `attribution.ts`). */
  cameFrom: (player: Player) => CameFrom;
  /** Open an in-app path (a "came from" deep link: the asset, its landing page, the Affiliates page). */
  onOpenPath: (path: string) => void;
};

const SKELETON_CARDS = 6;
const STAT_PLACEHOLDERS = Array.from({ length: STAT_COLUMNS }, (_, index) => index);

/**
 * The card's secondary line: when the player registered and where they came from.
 * Built as a `·`-joined string (not nested nodes) so it truncates as one line on a
 * narrow card. Each part is skipped when its field is absent, so an activity-only
 * row (present in the activity data set but not the registration list) renders what
 * it has rather than a dangling separator.
 *
 * Deliberately does NOT label a player as having no activity: on a real roster that
 * is most of the list, and repeating it down every card reads as a defect rather
 * than as information. The Activity filter is where that distinction belongs.
 */
const metaLine = (player: Player, t: TFunction, sourceLabel: SourceLabeller): string => {
  const source = sourceOf(player);
  return [
    player.registeredAt !== undefined ? t('Joined {{date}}', { date: prettyDate(player.registeredAt) }) : undefined,
    source !== undefined ? sourceLabel(channelOf(source)) : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The casino player roster — a column of cards at every width: avatar + email on top,
 * the figures labelled below (Deposits · Withdrawals · Net cash · Bet amount · Win
 * amount · GGR · NGR). Net cash = deposits less withdrawals. The fuller breakdown (cash,
 * bonuses, the affiliate's estimated commission) lives in the detail sheet, which each
 * card opens; the per-card copy button lifts the email to the clipboard without opening
 * it. The whole list sits in the studio's list panel.
 */
export const PlayersTable = ({
  players,
  loading = false,
  onCopyEmail,
  onSelect,
  emptyText,
  cameFrom,
  onOpenPath,
}: PlayersTableProps): React.ReactElement => {
  const { t } = useTranslation();
  const sourceLabel = useSourceLabel();

  // A "came from" deep link lives inside the card (itself a button): its click and its keys must not reach the card,
  // or Enter would open the sheet (the card prevents the key's default) instead of following the link. A plain click
  // opens the path in the app; a modified one (new tab or window) is left to the browser.
  const onLinkClick = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.stopPropagation();
    openInApp(event, path, onOpenPath);
  };
  const onLinkKey = (event: React.KeyboardEvent<HTMLAnchorElement>): void => event.stopPropagation();

  const cameNode = (came: CameFrom): React.ReactNode => {
    switch (came.kind) {
      case 'link': {
        const { path, via } = came;
        return (
          <>
            {path ? (
              <FromChip href={path} title={came.name} onClick={onLinkClick(path)} onKeyDown={onLinkKey}>
                <FromKind raw text={came.kindLabel} tag="span" />
                <FromName raw text={came.name} tag="span" />
              </FromChip>
            ) : (
              <FromMuted raw text={`${came.kindLabel} · ${came.name}`} tag="span" title={came.name} />
            )}
            {via ? (
              <FromVia>
                <FromViaWord text="via" tag="span" />
                {via.path ? (
                  <FromViaLink href={via.path} title={via.name} onClick={onLinkClick(via.path)} onKeyDown={onLinkKey}>
                    <Text raw text={via.name} tag="span" />
                  </FromViaLink>
                ) : (
                  <Text raw text={via.name} tag="span" title={via.name} />
                )}
              </FromVia>
            ) : null}
          </>
        );
      }
      case 'affiliate': {
        const { path, named } = came;
        if (!named) {
          return (
            <FromChip href={path} onClick={onLinkClick(path)} onKeyDown={onLinkKey}>
              <FromName raw text={came.label} tag="span" />
            </FromChip>
          );
        }
        // The affiliate who brought the player, and under it the campaign they came through.
        return (
          <>
            <FromChip href={path} title={named.affiliate} onClick={onLinkClick(path)} onKeyDown={onLinkKey}>
              <FromKind text="Affiliate" tag="span" />
              <FromName raw text={named.affiliate} tag="span" />
            </FromChip>
            <FromVia>
              <FromViaWord text="Campaign" tag="span" />
              <Text raw text={named.campaign} tag="span" title={named.campaign} />
            </FromVia>
          </>
        );
      }
      case 'pending':
        return <Skeleton width={112} height={24} radius={12} />;
      case 'organic':
      case 'direct':
      case 'other':
        return <FromMuted raw text={came.label} tag="span" title={came.label} />;
      default:
        return null;
    }
  };

  const onCopy = (email: string) => (event: React.MouseEvent<HTMLButtonElement>): void => {
    // Stop the click bubbling to the card (which would open the detail sheet).
    event.stopPropagation();
    onCopyEmail(email);
  };

  const onCardKey = (player: Player) => (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(player);
    }
  };

  if (loading) {
    return (
      <List>
        {Array.from({ length: SKELETON_CARDS }).map((_, index) => (
          <SkCard key={index}>
            <AvatarCell>
              <Skeleton width={40} height={40} circle />
            </AvatarCell>
            <SkIdentity>
              <Skeleton width="55%" height={15} radius={6} />
              <Skeleton width="38%" height={11} radius={5} />
            </SkIdentity>
            <SkCame>
              <Skeleton width={64} height={9} radius={5} />
              <Skeleton width={88} height={14} radius={6} />
            </SkCame>
            <SkStats>
              {STAT_PLACEHOLDERS.map((cell) => (
                <MetaCell key={cell}>
                  <Skeleton width={64} height={9} radius={5} />
                  <Skeleton width={88} height={14} radius={6} />
                </MetaCell>
              ))}
            </SkStats>
          </SkCard>
        ))}
      </List>
    );
  }

  if (players.length === 0) {
    return <EmptyState text={emptyText ?? t('No players match your search.')} />;
  }

  return (
    <List>
      {players.map((player) => {
        const came = cameFrom(player);
        const label = playerLabel(player, t);
        return (
          <Card
            key={player.id}
            role="button"
            tabIndex={0}
            aria-label={t('View {{email}}', { email: label })}
            onClick={() => onSelect(player)}
            onKeyDown={onCardKey(player)}
            $cameRow={came.kind !== 'none' && came.kind !== 'direct'}
          >
            <AvatarCell>
              <PlayerAvatar email={player.email || player.id} size={40} />
            </AvatarCell>

            <EmailCell>
              <EmailRow>
                <EmailText text={label} tag="span" title={label} />
                {player.email !== '' ? (
                  <CopyBtn type="button" aria-label={t('Copy email')} onClick={onCopy(player.email)}>
                    <Icon name="copy" size={14} />
                  </CopyBtn>
                ) : null}
              </EmailRow>
              <CardMeta>
                <MetaText text={metaLine(player, t, sourceLabel)} tag="span" />
              </CardMeta>
            </EmailCell>

            {came.kind !== 'none' ? (
              <CameCell $hideOnPhone={came.kind === 'direct'}>
                <CellLabel text="Came from" tag="span" />
                {cameNode(came)}
              </CameCell>
            ) : null}

            <Stats>
              <MetaCell>
                <CellLabel text="Deposits" tag="span" />
                <MoneyValue text={money(player.deposits, true)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="Withdrawals" tag="span" />
                <MoneyValue text={money(player.withdrawals, true)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="Net cash" tag="span" />
                <MoneyValue text={money(player.deposits - player.withdrawals, true)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="Bet amount" tag="span" />
                <MoneyValue text={money(player.wagered)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="Win amount" tag="span" />
                <MoneyValue text={money(player.wins)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="GGR" tag="span" />
                <MoneyValue text={money(player.ggr)} tag="span" />
              </MetaCell>
              <MetaCell>
                <CellLabel text="NGR" tag="span" />
                {player.ngr !== undefined ? (
                  <MoneyValue text={money(player.ngr)} tag="span" />
                ) : (
                  <NoValue raw text="–" tag="span" />
                )}
              </MetaCell>
            </Stats>
          </Card>
        );
      })}
    </List>
  );
};

export default PlayersTable;
